import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Loader2, FileText, Settings, Plus, Edit, X, BookOpen } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateChapterContent, generateChapterBrief, grammarCheck, editorialPass, proofreadChapter } from '../lib/ai-config';
import { parseBookOutline } from '../lib/outline-parser';
import { BookSettingsEditor } from './BookSettingsEditor';
import { ChapterNotesEditor } from './ChapterNotesEditor';
import { AddBonusSection } from './AddBonusSection';
import { ExportDialog } from './ExportDialog';
import type { ChapterType, SectionType } from '../lib/database.types';

interface Chapter {
  chapter_id: string;
  chapter_number: number | null;
  chapter_title: string;
  generated_text: string;
  status: string;
  word_count?: number;
  type: ChapterType;
  order_index: number;
  notes?: string;
  section_type?: SectionType;
  section_data?: Record<string, any>;
  chapter_references?: string;
}

interface Book {
  book_id: string;
  title: string;
  subtitle?: string;
  author?: string;
  author_bio?: string;
  chapters: Chapter[];
  audience?: string;
  perspective?: string;
  goals?: string;
  disclaimers?: string;
  rules?: string;
  writing_style_sample?: string;
  style_notes?: string;
  preferred_tone?: string[];
  ai_model?: string;
  target_word_count?: number;
  outline_text?: string;
}

function calculateWordCount(text: string): number {
  return text.trim().split(/\s+/).length;
}

function formatWordCount(count: number): string {
  return new Intl.NumberFormat().format(count);
}

export function BookViewer() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotesEditor, setShowNotesEditor] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [saving, setSaving] = useState(false);
  const [showBonusSection, setShowBonusSection] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editingReferences, setEditingReferences] = useState(false);
  const [editedReferences, setEditedReferences] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [outlineText, setOutlineText] = useState('');
  const [showResumePanel, setShowResumePanel] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState<{ current: number; total: number; status: string } | null>(null);

  // Generate chapters from a pasted outline (recovery flow for 0-chapter books)
  async function handleGenerateFromOutline() {
    if (!book || !outlineText.trim()) return;

    let parsed;
    try {
      parsed = parseBookOutline(outlineText);
    } catch (err: any) {
      alert(err.message || 'Failed to parse outline. Check the format and try again.');
      return;
    }

    if (parsed.chapters.length === 0) {
      alert('No chapters found in the outline. Make sure it contains chapter headings.');
      return;
    }

    setGenerating(true);
    setGenerateProgress({ current: 0, total: parsed.chapters.length, status: 'Starting...' });

    try {
      // Save outline to database so it's preserved even if generation fails
      await supabase.from('books_metadata').update({ outline_text: outlineText }).eq('book_id', book.book_id);

      const bookType = 'authority'; // default
      const priorChapterOpenings: string[] = [];
      const priorChapterBriefs: string[] = [];
      const usedCharacterNames: string[] = [];
      const allChapterTitles = parsed.chapters.map((ch) => {
        if (ch.type === 'chapter' && ch.chapter_number) {
          return `Chapter ${ch.chapter_number}: ${ch.title}`;
        }
        return ch.title;
      });

      // Build list of existing chapter titles (normalized) to skip duplicates
      const existingTitles = new Set(
        (book.chapters || []).map(ch => ch.chapter_title.toLowerCase().trim())
      );

      // Seed prior context from existing chapters
      for (const ch of (book.chapters || []).sort((a, b) => a.order_index - b.order_index)) {
        if (ch.generated_text) {
          const opening = ch.generated_text.trim().split(/\s+/).slice(0, 50).join(' ');
          priorChapterOpenings.push(`${ch.chapter_title}: ${opening}`);
          priorChapterBriefs.push(`${ch.chapter_title}: ${ch.generated_text.slice(0, 300)}`);
        }
      }

      for (let i = 0; i < parsed.chapters.length; i++) {
        const chapter = parsed.chapters[i];
        const chapterTitle =
          chapter.type === 'chapter' && chapter.chapter_number
            ? `Chapter ${chapter.chapter_number}: ${chapter.title}`
            : chapter.title;

        // Skip chapters that already exist
        if (existingTitles.has(chapterTitle.toLowerCase().trim()) || existingTitles.has(chapter.title.toLowerCase().trim())) {
          console.log(`[resume] Skipping existing chapter: ${chapterTitle}`);
          continue;
        }

        // Step 1: Plan
        setGenerateProgress({ current: i + 1, total: parsed.chapters.length, status: `Planning "${chapterTitle}"...` });

        const chapterBrief = await generateChapterBrief({
          bookTitle: book.title,
          chapterTitle,
          chapterType: chapter.type,
          chapterNotes: chapter.content.join('\n'),
          audience: book.audience || '',
          perspective: book.perspective,
          goals: book.goals || '',
          target_word_count: book.target_word_count,
          bookType,
          priorChapterBriefs,
        });

        priorChapterBriefs.push(`${chapterTitle}: ${chapterBrief.slice(0, 300)}`);

        // Step 2: Write
        setGenerateProgress({ current: i + 1, total: parsed.chapters.length, status: `Writing "${chapterTitle}"...` });

        const generatedContent = await generateChapterContent({
          model: book.ai_model || 'claude-opus-4-6',
          bookTitle: book.title,
          audience: book.audience || '',
          perspective: book.perspective || '',
          goals: book.goals || '',
          disclaimers: book.disclaimers,
          rules: book.rules || '',
          writingStyleSample: book.writing_style_sample,
          styleNotes: book.style_notes,
          preferredTone: book.preferred_tone || ['Professional but friendly'],
          chapterTitle,
          chapterType: chapter.type as any,
          chapterNotes: chapter.content.join('\n'),
          target_word_count: book.target_word_count,
          bookType,
          priorChapterOpenings,
          chapterBrief,
        });

        // Step 3: Polish (grammar -> editorial -> proofread)
        setGenerateProgress({ current: i + 1, total: parsed.chapters.length, status: `Polishing "${chapterTitle}"...` });

        const { correctedText: grammarCorrected } = await grammarCheck(generatedContent);
        const editorialCorrected = await editorialPass({
          text: grammarCorrected,
          bookTitle: book.title,
          chapterTitle,
          priorChapterSummaries: priorChapterBriefs,
        });
        const polishedContent = await proofreadChapter({
          content: editorialCorrected,
          bookTitle: book.title,
          chapterTitle,
          chapterType: chapter.type,
          allChapterTitles,
          previouslyUsedNames: usedCharacterNames,
        });

        // Extract names for collision detection
        const namePattern = /(?:named|called|meet|introduces?|friend|neighbor|client|patient|colleague)\s+([A-Z][a-z]+)/g;
        let nameMatch;
        while ((nameMatch = namePattern.exec(polishedContent)) !== null) {
          const name = nameMatch[1];
          if (!usedCharacterNames.includes(name)) usedCharacterNames.push(name);
        }

        const opening = polishedContent.trim().split(/\s+/).slice(0, 50).join(' ');
        priorChapterOpenings.push(`${chapterTitle}: ${opening}`);

        // Save to database
        setGenerateProgress({ current: i + 1, total: parsed.chapters.length, status: `Saving "${chapterTitle}"...` });

        const { error: chapterError } = await supabase.from('books_chapters').insert({
          book_id: book.book_id,
          chapter_number: chapter.chapter_number || null,
          chapter_title: chapter.title,
          notes: chapter.content.join('\n'),
          generated_text: polishedContent,
          status: 'completed',
          chapter_type: chapter.type,
          order_index: (book.chapters?.length || 0) + i,
        });

        if (chapterError) throw chapterError;
      }

      // Refresh the book data
      await fetchBook();
      setShowResumePanel(false);
      setGenerateProgress(null);
    } catch (error) {
      console.error('Error generating chapters:', error);
      alert('Error generating chapter. Chapters that completed are saved. You can paste the outline again to retry — already-generated chapters will remain.');
      await fetchBook(); // refresh to show any chapters that did complete
    } finally {
      setGenerating(false);
      setGenerateProgress(null);
    }
  }

  // Initial data fetch with ordered chapters
  useEffect(() => {
    async function fetchData() {
      try {
        if (!bookId) return;

        const { data: book, error: bookError } = await supabase
          .from('books_metadata')
          .select(`
            book_id,
            title,
            subtitle,
            author,
            author_bio,
            audience,
            perspective,
            goals,
            disclaimers,
            rules,
            writing_style_sample,
            style_notes,
            preferred_tone,
            ai_model,
            target_word_count,
            outline_text,
            chapters:books_chapters(
              chapter_id,
              chapter_number,
              chapter_title,
              notes,
              status,
              generated_text,
              chapter_type,
              section_type,
              section_data,
              order_index,
              chapter_references
            )
          `)
          .eq('book_id', bookId)
          .order('order_index', { foreignTable: 'chapters' })
          .single();

        if (bookError) throw bookError;
        
        if (book) {
          // Sort chapters by type and order_index
          const sortedChapters = [...book.chapters].sort((a, b) => {
            // Introduction always comes first
            if (a.chapter_type === 'introduction') return -1;
            if (b.chapter_type === 'introduction') return 1;
            
            // Then sort by order_index
            return a.order_index - b.order_index;
          });

          // Calculate word count for each chapter
          const chaptersWithWordCount = sortedChapters.map(chapter => ({
            ...chapter,
            word_count: chapter.generated_text ? calculateWordCount(chapter.generated_text) : 0
          }));

          const processedBook = {
            ...book,
            chapters: chaptersWithWordCount
          };

          setBook(processedBook);

          // Pre-populate outline from saved data
          if (book.outline_text) {
            setOutlineText(book.outline_text);
          }

          // Set the first chapter (which should be Introduction) as selected
          if (chaptersWithWordCount.length > 0) {
            setSelectedChapter(chaptersWithWordCount[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching book:', error);
        alert('Error fetching book. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [bookId]);

  // Close editing mode when switching chapters
  useEffect(() => {
    if (editingContent) {
      setEditingContent(false);
      setEditedContent('');
    }
    if (editingReferences) {
      setEditingReferences(false);
      setEditedReferences('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter?.chapter_id]);

  async function handleSaveNotes(notes: string, shouldRegenerate: boolean) {
    if (!selectedChapter || !book) return;

    setSaving(true);
    try {
      // First update the notes
      const { error: updateError } = await supabase
        .from('books_chapters')
        .update({ notes })
        .eq('chapter_id', selectedChapter.chapter_id);

      if (updateError) throw updateError;

      // If regeneration is requested, generate new content
      if (shouldRegenerate) {
        const generatedContent = await generateChapterContent({
          model: book.ai_model || 'claude-sonnet-4-6',
          bookTitle: book.title,
          audience: book.audience || '',
          perspective: book.perspective || '',
          goals: book.goals || '',
          disclaimers: book.disclaimers,
          rules: book.rules || '',
          writingStyleSample: book.writing_style_sample,
          styleNotes: book.style_notes,
          preferredTone: book.preferred_tone || ['Professional but friendly'],
          chapterTitle: selectedChapter.chapter_title,
          chapterType: (selectedChapter.chapter_type || 'chapter') as any,
          chapterNotes: notes,
          target_word_count: book.target_word_count,
          chapterReferences: selectedChapter.chapter_references,
          priorChapterOpenings: book.chapters
            .filter((ch) => ch.chapter_id !== selectedChapter.chapter_id && ch.generated_text)
            .map((ch) => {
              const firstSentences = ch.generated_text.split(/(?<=[.!?])\s+/).slice(0, 2).join(' ');
              return ch.chapter_title + ': ' + firstSentences;
            }),
        });

        // Grammar check
        const { correctedText: grammarCorrected } = await grammarCheck(generatedContent);

        // Editorial pass
        const editorialCorrected = await editorialPass({
          text: grammarCorrected,
          bookTitle: book.title,
          chapterTitle: selectedChapter.chapter_title,
        });

        // Proofread
        const allChapterTitles = book.chapters.map((ch) => ch.chapter_title);
        const polishedContent = await proofreadChapter({
          content: editorialCorrected,
          bookTitle: book.title,
          chapterTitle: selectedChapter.chapter_title,
          chapterType: selectedChapter.chapter_type || 'chapter',
          allChapterTitles,
          previouslyUsedNames: [],
        });

        const { error: contentError } = await supabase
          .from('books_chapters')
          .update({
            generated_text: polishedContent,
            status: 'completed'
          })
          .eq('chapter_id', selectedChapter.chapter_id);

        if (contentError) throw contentError;
      }

      // Refresh book data
      await fetchBook();
      setShowNotesEditor(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveContent() {
    if (!selectedChapter || !book) return;

    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('books_chapters')
        .update({ generated_text: editedContent })
        .eq('chapter_id', selectedChapter.chapter_id);

      if (updateError) throw updateError;

      // Update the selected chapter immediately with new word count
      setSelectedChapter({
        ...selectedChapter,
        generated_text: editedContent,
        word_count: calculateWordCount(editedContent)
      });

      setEditingContent(false);
      await fetchBook(); // Refresh all data
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function fetchBook() {
    try {
      if (!bookId) return;

      const { data: book, error: bookError } = await supabase
        .from('books_metadata')
        .select(`
          book_id,
          title,
          subtitle,
          author,
          author_bio,
          audience,
          perspective,
          goals,
          disclaimers,
          rules,
          writing_style_sample,
          style_notes,
          preferred_tone,
          ai_model,
          target_word_count,
          chapters:books_chapters(
            chapter_id,
            chapter_number,
            chapter_title,
            notes,
            status,
            generated_text,
            chapter_type,
            section_type,
            section_data,
            order_index,
            chapter_references
          )
        `)
        .eq('book_id', bookId)
        .single();

      if (bookError) throw bookError;

      // Calculate word count for each chapter
      const chaptersWithWordCount = book.chapters.map(chapter => ({
        ...chapter,
        word_count: chapter.generated_text ? calculateWordCount(chapter.generated_text) : 0
      }));

      const processedBook = {
        ...book,
        chapters: chaptersWithWordCount
      };

      setBook(processedBook);

      // Update selected chapter if it exists
      if (selectedChapter) {
        const updatedChapter = chaptersWithWordCount.find(ch => ch.chapter_id === selectedChapter.chapter_id);
        if (updatedChapter) {
          setSelectedChapter(updatedChapter);
        }
      }
    } catch (error) {
      console.error('Error fetching book:', error);
      alert('Error fetching book. Please try again.');
    }
  }

  function handleCancelEdit() {
    setEditingContent(false);
    setEditedContent('');
  }

  async function handleSaveReferences() {
    if (!selectedChapter || !book) return;
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('books_chapters')
        .update({ chapter_references: editedReferences || null })
        .eq('chapter_id', selectedChapter.chapter_id);
      if (updateError) throw updateError;
      setSelectedChapter({ ...selectedChapter, chapter_references: editedReferences || undefined });
      setEditingReferences(false);
      await fetchBook();
    } catch (error) {
      console.error('Error saving references:', error);
      alert('Error saving references. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddBonusSection(data: {
    chapter_title: string;
    chapter_type: ChapterType;
    section_type: SectionType;
    notes?: string;
  }) {
    if (!book) return;

    setSaving(true);
    try {
      // Get the highest order index
      const maxOrder = Math.max(...book.chapters.map(ch => ch.order_index));

      // Create the new chapter
      const { error: chapterError } = await supabase
        .from('books_chapters')
        .insert({
          book_id: book.book_id,
          chapter_title: data.chapter_title,
          chapter_type: data.chapter_type,
          section_type: data.section_type,
          notes: data.notes,
          order_index: maxOrder + 1,
        });

      if (chapterError) throw chapterError;

      // Refresh book data
      await fetchBook();
      setShowBonusSection(false);
    } catch (error) {
      console.error('Error adding bonus section:', error);
      alert('Error adding bonus section. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function getTotalWordCount(): number {
    if (!book) return 0;
    return book.chapters.reduce((total, chapter) => total + (chapter.word_count || 0), 0);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Book not found</h2>
          <p className="mt-2 text-gray-600">This book may have been deleted or you don't have access to it.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </button>
              <h1 className="ml-4 text-2xl font-bold text-gray-900">{book.title}</h1>
              <div className="ml-4 text-sm text-gray-500">
                Total Words: {formatWordCount(getTotalWordCount())}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {book.chapters.length > 0 && !showResumePanel && (
                <button
                  onClick={() => setShowResumePanel(true)}
                  className="inline-flex items-center px-4 py-2 border border-indigo-600 text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Continue Generation
                </button>
              )}
              <button
                onClick={() => setShowBonusSection(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Bonus Section
              </button>
              <button
                onClick={() => setShowExportDialog(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          <div className="w-64 flex-shrink-0">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {book.chapters
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((chapter) => (
                    <li
                      key={chapter.chapter_id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        selectedChapter?.chapter_id === chapter.chapter_id ? 'bg-gray-50' : ''
                      }`}
                      onClick={() => setSelectedChapter(chapter)}
                    >
                      <div className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {chapter.chapter_number ? `Chapter ${chapter.chapter_number}: ` : ''}{chapter.chapter_title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatWordCount(chapter.word_count || 0)} words
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className="flex-1">
            {(book.chapters.length === 0 || showResumePanel) && !generating && (
              <div className="bg-white shadow rounded-lg overflow-hidden p-6">
                <div className="text-center mb-6">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {book.chapters.length === 0 ? 'No chapters generated yet' : 'Continue Generating Chapters'}
                  </h2>
                  <p className="mt-2 text-gray-600">
                    {book.chapters.length === 0
                      ? 'Paste your outline below to generate all chapters.'
                      : `${book.chapters.length} chapter${book.chapters.length === 1 ? '' : 's'} already generated. Paste the full outline below and any missing chapters will be generated. Existing chapters will be skipped.`}
                  </p>
                </div>
                <textarea
                  value={outlineText}
                  onChange={(e) => setOutlineText(e.target.value)}
                  placeholder={'Paste your outline here. Example format:\n\nIntroduction\n- Key points for the intro\n\nChapter 1: First Chapter Title\n- Topic to cover\n- Another point\n\nChapter 2: Second Chapter Title\n- Topics here\n\nConclusion\n- Wrap-up points'}
                  className="w-full h-64 p-4 border border-gray-300 rounded-md font-mono text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="mt-4 flex justify-between">
                  {showResumePanel && (
                    <button
                      onClick={() => { setShowResumePanel(false); setOutlineText(''); }}
                      className="inline-flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  )}
                  <div className="ml-auto">
                    <button
                      onClick={handleGenerateFromOutline}
                      disabled={!outlineText.trim()}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BookOpen className="h-5 w-5 mr-2" />
                      {book.chapters.length === 0 ? 'Generate Chapters' : 'Generate Missing Chapters'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {generating && generateProgress && (
              <div className="bg-white shadow rounded-lg overflow-hidden p-6">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Generating Chapter {generateProgress.current} of {generateProgress.total}
                  </h2>
                  <p className="mt-2 text-gray-600">{generateProgress.status}</p>
                  <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((generateProgress.current / generateProgress.total) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    This may take several minutes. Completed chapters are saved as they finish.
                  </p>
                </div>
              </div>
            )}

            {selectedChapter && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {selectedChapter.chapter_number ? `Chapter ${selectedChapter.chapter_number}: ` : ''}{selectedChapter.chapter_title}
                      </h2>
                      <div className="text-sm text-gray-500">
                        {formatWordCount(selectedChapter.word_count || 0)} words
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => setShowNotesEditor(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Notes
                      </button>
                      {!editingContent ? (
                        <button
                          onClick={() => {
                            setEditedContent(selectedChapter.generated_text || '');
                            setEditingContent(true);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Content
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveContent}
                            disabled={saving}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Content
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <div className="prose max-w-none">
                    {editingContent ? (
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="w-full h-[500px] p-4 border rounded-md font-mono"
                      />
                    ) : (
                      <div className="whitespace-pre-wrap">
                        {selectedChapter.generated_text}
                      </div>
                    )}
                  </div>
                </div>

                {/* Chapter References Section */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Chapter References</h3>
                    {!editingReferences ? (
                      <button
                        onClick={() => {
                          setEditedReferences(selectedChapter.chapter_references || '');
                          setEditingReferences(true);
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit References
                      </button>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => { setEditingReferences(false); setEditedReferences(''); }}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveReferences}
                          disabled={saving}
                          className="inline-flex items-center px-2 py-1 text-xs border border-transparent rounded text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                  {editingReferences ? (
                    <textarea
                      value={editedReferences}
                      onChange={(e) => setEditedReferences(e.target.value)}
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-md text-sm"
                      placeholder="Add references for this chapter (author, title, year, URL — one per line)"
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      {selectedChapter.chapter_references ? (
                        <pre className="whitespace-pre-wrap font-sans">{selectedChapter.chapter_references}</pre>
                      ) : (
                        <p className="italic">No references for this chapter.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSettings && (
        <BookSettingsEditor
          bookId={book.book_id}
          initialData={{
            title: book.title,
            subtitle: book.subtitle,
            author: book.author,
            author_bio: book.author_bio,
            audience: book.audience,
            perspective: book.perspective || 'Third-person limited',
            goals: book.goals,
            disclaimers: book.disclaimers,
            rules: book.rules,
            writing_style_sample: book.writing_style_sample,
            style_notes: book.style_notes,
            preferred_tone: book.preferred_tone || ['Professional but friendly'],
            ai_model: book.ai_model || 'GPT-4o',
            target_word_count: book.target_word_count || 1000,
          }}
          onClose={() => setShowSettings(false)}
          onSaved={fetchBook}
        />
      )}

      {showNotesEditor && selectedChapter && (
        <ChapterNotesEditor
          isOpen={showNotesEditor}
          onClose={() => setShowNotesEditor(false)}
          onSave={handleSaveNotes}
          notes={selectedChapter.notes || ''}
          title={selectedChapter.chapter_title}
          saving={saving}
        />
      )}

      {showBonusSection && (
        <AddBonusSection
          isOpen={showBonusSection}
          onClose={() => setShowBonusSection(false)}
          onAdd={handleAddBonusSection}
          saving={saving}
        />
      )}

      {showExportDialog && (
        <ExportDialog
          isOpen={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          book={book}
        />
      )}
    </div>
  );
}